/**
 * UsersApiContext.js
 *
 * Provides a React context for performing user-related API operations.
 * Handles token refresh logic and centralizes user management requests.
 */
import React, { createContext, useMemo } from "react";
import { useAuth } from "utils/hooks";
import API_ENDPOINTS from "../../api/apiEndpoints";

import * as userApi from "../../api/usersApiRequests"; // Import everything as `postsApi`

/**
 * @typedef {Object} UsersApiClient
 * @property {function(): Promise<Object>} getUsers - Get all user details.
 * @property {function(): Promise<Object>} getGroups - Get all groups.
 * @property {function(number=): Promise<Object>} getGroupsDetails - Get detailed group info.
 * @property {function(): Promise<Object>} getUserProfile - Get current user's profile.
 * @property {function(string): Promise<Object>} updateDefaultTenant - Update the user's default tenant.
 * @property {function(string): Promise<Object>} deleteUserById - Delete a user by identifier.
 * @property {function(Object): Promise<Object>} updateUserDetails - Update user details.
 * @property {function(Object): Promise<Object>} sendInvitation - Invite a user to register.
 * @property {function(string, Object): Promise<Object>} registerUser - Register a new user.
 * @property {function(string, string): Promise<Object>} verifyRegisterToken - Verify registration token.
 * @property {function(): Promise<Object>} getAvailableRoles - Fetch available roles.
 * @property {function(Object): Promise<Object>} createGroup - Create a user group.
 * @property {function(string): Promise<Object>} deleteGroup - Delete a user group by name.
 */

/**
 * React Context for User API operations.
 * @type {React.Context<UsersApiClient>}
 */
export const UsersApiContext = createContext();

/**
 * UsersApiProvider - Provider for Users API operations.
 * @exports UsersApiProvider
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
export const UsersApiProvider = ({ children }) => {
  const {
    selected_tenant_id: tenantId,
    tenant_access_token: access_token,
    general_access_token,
    onRefreshToken,
  } = useAuth();
  const baseUrl = API_ENDPOINTS.usersApi.baseUrl;

  /**
   * Handles API responses. If 401 is encountered, attempts token refresh and retries.
   *
   * @param {Response} response - The fetch response.
   * @param {Function|null} retryFunction - Retry callback with new token.
   * @returns {Promise<Object>} Parsed JSON response or error.
   */
  const handleResponse = async (response, retryFunction) => {
    if (!response.ok && retryFunction !== null) {
      // Check if the error is due to an expired token
      if (response.code === 401) {
        // Try refreshing the token

        const refreshResponse = await onRefreshToken();

        if (refreshResponse.access_token) {
          // Retry the original request with the new token
          // Retry the original request with the new token
          const retryResp = await retryFunction(refreshResponse.access_token);
          const retryData = await retryResp.json();

          return retryData;
        } else {
          return { status: response.status, message: "You are unauthorized" };
        }
      }
      if (response.code === 403) {
        // Try refreshing the token

        return { status: response.status, message: "You don't have rights" };
      }
    }

    return response.json();
  };

  /** @type {UsersApiClient} */
  const usersApiClient = useMemo(
    () => ({
      /**
       * Delete a user by their email.
       * @param {string} emailAddress - Email of the user to delete.
       * @returns {Promise<Object>}
       */
      leaveTenantById: async (tenantId) => {
        const response = await userApi.leaveTenant(
          tenantId,
          general_access_token,
        );
        return handleResponse(response);
      },

      /**
       * Get all user details for the tenant.
       * @returns {Promise<Object>}
       */
      getUsers: async () => {
        const response = await userApi.getAllUsersDetails(
          tenantId,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.getAllUsersDetails(tenantId, newToken),
        );
      },

      recoverPassword: async (emailAddress) => {
        const response = await userApi.recoverPassword(emailAddress);
        return handleResponse(response);
      },

      /**
       * Verify a user password recovery token.
       * @param {string} userId - User ID.
       * @param {string} recoverToken - Token to verify.
       * @returns {Promise<Object>}
       */
      verifyRecoverToken: async (recoverToken) => {
        const response = await userApi.verifyRecoverToken(recoverToken);
        return handleResponse(response);
      },

      /**
       * Change the password of a user
       * @param {string} userId - User ID.
       * @param {string} payload - New details for change password.
       * @returns {Promise<Object>}
       */
      changePassword: async (password, recoverToken) => {
        const response = await userApi.changePassword(password, recoverToken);
        return handleResponse(response);
      },

      /**
       * Get all groups for the tenant.
       * @returns {Promise<Object>}
       */
      getGroups: async () => {
        const response = await userApi.getGroups(tenantId, access_token);
        return handleResponse(response, (newToken) =>
          userApi.getGroups(tenantId, newToken),
        );
      },

      /**
       * Get detailed group info.
       * @param {number} [limit=0] - Optional limit.
       * @returns {Promise<Object>}
       */
      getGroupsDetails: async (limit = 0) => {
        const response = await userApi.getGroupsDetails(
          tenantId,
          limit,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.getGroupsDetails(tenantId, limit, newToken),
        );
      },

      /**
       * Get current user's profile.
       * @returns {Promise<Object>}
       */
      getUserProfile: async (userId) => {
        const response = await userApi.getUserProfile(
          tenantId,
          userId,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.getUserProfile(tenantId, userId, newToken),
        );
      },

      /**
       * Update current user's profile.
       * @returns {Promise<Object>}
       */
      updateUserProfile: async (payload) => {
        const response = await userApi.updateUserProfile(
          payload,
          general_access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.updateUserProfile(payload, newToken),
        );
      },

      /**
       * Enable or disable email notifications.
       * @returns {Promise<Object>}
       */
      enableNotifications: async (isEnabled) => {
        const response = await userApi.enableNotifications(
          isEnabled,
          general_access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.enableNotifications(isEnabled, newToken),
        );
      },

      /**
       * Update current user's profile.
       * @returns {Promise<Object>}
       */
      updateUserPassword: async (payload) => {
        const response = await userApi.updateUserPassword(
          payload,
          general_access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.updateUserPassword(payload, newToken),
        );
      },

      /**
       * Update the default tenant for the user.
       * @param {string} tenant - New tenant ID.
       * @returns {Promise<Object>}
       */
      updateDefaultTenant: async (payload) => {
        const response = await userApi.updateDefaultTenant(
          payload.tenantId,
          payload.userId,
          payload.tenantName,
          access_token,
        );

        return handleResponse(response, (newToken) =>
          userApi.updateDefaultTenant(
            payload.tenantId,
            payload.userId,
            payload.tenantName,
            newToken,
          ),
        );
      },

      /**
       * Delete a user by their email.
       * @param {string} emailAddress - Email of the user to delete.
       * @returns {Promise<Object>}
       */
      deleteUserById: async (userId) => {
        const response = await userApi.deleteUserById(
          tenantId,
          userId,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.deleteUserById(tenantId, userId, newToken),
        );
      },

      /**
       * Update user details.
       * @param {Object} userDetails - Updated user data.
       * @returns {Promise<Object>}
       */
      updateUserDetails: async (userId, userDetails) => {
        const response = await userApi.updateUsersDetails(
          tenantId,
          userId,
          userDetails,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.updateUsersDetails(tenantId, userId, userDetails, newToken),
        );
      },

      /**
       * Add user to a group in a tenant.
       * @param {Object} payload - Updated groups data.
       * @returns {Promise<Object>}
       */
      addUserToGroup: async (groupId, payload) => {
        const response = await userApi.addUserToGroup(
          tenantId,
          groupId,
          payload,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.addUserToGroup(tenantId, groupId, payload, newToken),
        );
      },

      /**
       * Remove user from a group
       * @param {Object} payload - Updated groups data.
       * @returns {Promise<Object>}
       */
      removeUserFromGroup: async (groupId, payload) => {
        const response = await userApi.removeUserFromGroup(
          tenantId,
          groupId,
          payload,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.removeUserFromGroup(tenantId, groupId, payload, newToken),
        );
      },

      /**
       * Update user in a group
       * @param {Object} payload - Updated groups data.
       * @returns {Promise<Object>}
       */
      updateUserInGroup: async (groupId, userId, payload) => {
        const response = await userApi.updateUserInGroup(
          tenantId,
          groupId,
          userId,
          payload,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.updateUserInGroup(
            tenantId,
            groupId,
            userId,
            payload,
            newToken,
          ),
        );
      },

      /**
       * Send an invitation to register a user.
       * @param {Object} userDetails - New user data.
       * @returns {Promise<Object>}
       */
      sendInvitation: async (userDetails) => {
        const response = await userApi.sendRegisterInvitation(
          tenantId,
          userDetails,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.sendRegisterInvitation(tenantId, userDetails, newToken),
        );
      },

      /**
       * Register a user (used by invited user).
       * @param {string} tenantId - Tenant ID.
       * @param {Object} userDetails - Registration details.
       * @returns {Promise<Object>}
       */
      registerUser: async (tenantId, registerToken, userDetails) => {
        const response = await userApi.registerUser(
          tenantId,
          registerToken,
          userDetails,
        );
        return handleResponse(response);
      },

      /**
       * Register a owner .
       * @param {Object} ownerDetails - Registration details.
       * @returns {Promise<Object>}
       */
      registerOwner: async (tenantId, registerToken, ownerDetails) => {
        const response = await userApi.registerOwner(
          tenantId,
          registerToken,
          ownerDetails,
        );
        return handleResponse(response);
      },

      /**
       * Verify a user registration token.
       * @param {string} tenantId - Tenant ID.
       * @param {string} registerToken - Token to verify.
       * @returns {Promise<Object>}
       */
      verifyOwnerRegisterToken: async (tenantId, registerToken) => {
        const response = await userApi.verifyOwnerRegisterToken(
          tenantId,
          registerToken,
        );
        return handleResponse(response);
      },

      /**
       * Verify a user registration token.
       * @param {string} tenantId - Tenant ID.
       * @param {string} registerToken - Token to verify.
       * @returns {Promise<Object>}
       */
      verifyUserRegisterToken: async (tenantId, registerToken) => {
        const response = await userApi.verifyUserRegisterToken(
          tenantId,
          registerToken,
        );
        return handleResponse(response);
      },

      /**
       * Get list of available roles.
       * @returns {Promise<Object>}
       */
      getAvailableRoles: async () => {
        const response = await userApi.getAvailableRoles(access_token);
        return handleResponse(response, (newToken) =>
          userApi.getAvailableRoles(newToken),
        );
      },

      /**
       * Create a new user group.
       * @param {Object} groupDetails - Group configuration.
       * @returns {Promise<Object>}
       */
      createGroup: async (groupDetails) => {
        const response = await userApi.createGroup(
          tenantId,
          groupDetails,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.createGroup(tenantId, groupDetails, newToken),
        );
      },

      /**
       * Delete a user group by name.
       * @param {string} groupName - Name of the group to delete.
       * @returns {Promise<Object>}
       */
      deleteGroup: async (groupId) => {
        const response = await userApi.deleteGroup(
          tenantId,
          groupId,
          access_token,
        );
        return handleResponse(response, (newToken) =>
          userApi.deleteGroup(tenantId, groupId, newToken),
        );
      },
    }),
    [baseUrl, access_token],
  );

  return (
    <UsersApiContext.Provider value={usersApiClient}>
      {children}
    </UsersApiContext.Provider>
  );
};
